/**
 * Application state.
 *
 * One store, but with a typed shape and a subscribe/notify boundary, so a feature
 * module reads what it needs and cannot silently reach into another's slice --
 * v2 had a single global mutable object that any of its 352 functions could rewrite.
 */

import { DEFAULT_MIN_PLAYERS } from "../ui/router";
import type {
  Archetype,
  AvailabilityProfile,
  BuildSuggestions,
  Card,
  CardAvailability,
  CardFacets,
  DeckPayload,
  DeckSummary,
  FormatView,
  LegendChoice,
  LegendSort,
  LegendMeta,
  LegendMatchups,
  MatchupOverview,
  MetaDeck,
  MetaStatus,
  OpeningOdds,
  RefreshStatus,
  SmartSession,
  CardDetail,
  CardTrendOverview,
  ChampionMeta,
  TournamentDetail,
  TrendBucket,
  TrendDimension,
  TrendOverview,
  Validation,
} from "../api/types";

export interface CardFilters {
  q: string;
  cardType: string;
  domain: string;
  setCode: string;
  rarity: string;
  availableOnly: boolean;
  sort: "name" | "cost" | "availability";
}

/** Which top-level view is showing. */
export type ViewName = "find" | "explore" | "build" | "decks" | "search";

/** How the deck search orders its results. Rank is the default everywhere else ranks. */
export type DeckSearchSort = "rank" | "recency";

/**
 * Explore answers two different questions and must not blur them.
 *
 * "legends" ranks the field by what is winning -- shares that partition the metagame.
 * "cards" reports what is being played -- adoption, which does not. "matchups" reports
 * what beats what, from recorded matches rather than from published lists, which is a
 * third population again. Same page, deliberately separate numbers.
 */
export type ExploreMode = "legends" | "cards" | "matchups";

export interface AppState {
  ready: boolean;
  error: string;
  /**
   * Set when the running server is older than this page.
   *
   * Sticky and non-dismissable: every feature added since that server started will
   * misbehave, and the failures look like unrelated bugs rather than one stale process.
   */
  staleServer: string;
  view: ViewName;
  notice: string;
  formats: FormatView[];
  facets: CardFacets | null;

  deckId: string;
  deck: DeckPayload;
  validation: Validation | null;
  dirty: boolean;
  savedDecks: DeckSummary[];

  availability: AvailabilityProfile | null;

  filters: CardFilters;
  cards: CardAvailability[];
  cardTotal: number;
  cardsLoading: boolean;
  cardLimit: number;
  builderReview: boolean;
  /** Cards referenced by the deck, resolved for display. */
  deckCards: Map<string, CardAvailability>;
  /** What to add next, for the deck as it stands. Null until the first fetch lands. */
  suggestions: BuildSuggestions | null;
  /**
   * Exact opening-hand odds for the deck on the bench.
   *
   * Server-computed and hypergeometric. The simulator deals real hands on top of
   * these; it must never be used to *estimate* them, or the page would disagree
   * with itself between reloads.
   */
  opening: OpeningOdds | null;
  /** Whether the card drawer is showing. Closed, the deck gets the whole width. */
  drawerOpen: boolean;
  /**
   * Where the deck on the bench came from, when it came from somewhere addressable.
   *
   * A deck opened from Explore has a short, durable link of its own. Without this the
   * address would immediately become the 900-character self-contained form, which is
   * a worse link to have in your bar and a worse one to copy. `signature` is the deck
   * as it arrived: once it stops matching, the deck has been edited and is no longer
   * the thing that id names, so the self-contained link takes over.
   */
  benchSource: { deckId: string; signature: string } | null;

  metaStatus: MetaStatus | null;
  archetypes: Archetype[];
  metaDecks: MetaDeck[];
  metaLoading: boolean;
  /** Archetype currently expanded in the meta view; "" shows the ranked overview. */
  metaArchetype: string;
  metaBuildableOnly: boolean;

  /** Smart Decks. `smartAnswers` is the in-progress round, keyed by cardId. */
  smartLegends: LegendChoice[];
  smartSession: SmartSession | null;
  smartAnswers: Map<string, number>;
  smartTouched: Set<string>;
  smartBusy: boolean;
  smartFinished: boolean;
  /**
   * Which wizard screen to show. A buildable deck stops on the compact "ready" screen;
   * "rounds" is only the question flow before that, or an explicit request to keep
   * looking for a stronger option.
   */
  smartShowing: "rounds" | "ready";
  /**
   * Unfinished sessions, so one survives a closed tab. The answers are the expensive
   * part — three rounds pin down roughly 75 cards — and until this was surfaced they
   * were written to the database and then unreachable.
   */
  smartResumable: SmartSession[];
  /** Legend picker filter, so a 49-legend list stays navigable. */
  smartLegendQuery: string;
  smartLegendSort: LegendSort;
  /**
   * Why the legend list is empty, when it is.
   *
   * Distinct from "there are no legends": an empty list because the request failed is
   * a different problem with a different fix, and telling somebody to run the meta
   * pipeline when the real fault is a stale server sends them somewhere useless.
   */
  smartLegendsError: string;
  smartLegendsLoaded: boolean;
  /**
   * How many times loading the legends has failed in a row.
   *
   * Drives the difference between "that did not work, try again" and "that has not
   * worked twice, here is the thing to actually go and do". Without it a retry that
   * fails identically looks like a button that does nothing.
   */
  smartLegendsAttempts: number;
  smartLegendsRetrying: boolean;
  refresh: RefreshStatus | null;
  refreshBusy: boolean;

  exploreDimension: TrendDimension;
  exploreFormat: string;
  exploreFrom: string;
  exploreTo: string;
  exploreMinPlayers: number;
  /**
   * The chosen range, resolved by the server: "" for its default, "all", or a day count.
   *
   * Held as the request rather than as two computed dates, because computing them here
   * needs the archive's span, and not having it yet is what made "All time" ask for
   * nothing and get ninety days back.
   */
  exploreRange: string;
  exploreBucket: TrendBucket;
  trendOverview: TrendOverview | null;
  /** Which question Explore is answering: what is winning, or what is being played. */
  exploreMode: ExploreMode;
  /** The card wall, and the one card being read. */
  cardTrends: CardTrendOverview | null;
  cardDetail: CardDetail | null;
  exploreCardType: string;
  championMeta: ChampionMeta | null;
  legendMeta: LegendMeta | null;
  tournamentDetail: TournamentDetail | null;
  exploreLoading: boolean;
  exploreError: string;

  /**
   * Legend matchups, and the one legend opened out of them.
   *
   * Held apart from `trendOverview` on purpose: these come from recorded matches
   * over the upstream set window, not from the published lists inside the date range
   * the rest of Explore filters by. Sharing a slice would invite a filter that
   * silently applies to one number and not the other.
   */
  matchups: MatchupOverview | null;
  legendMatchups: LegendMatchups | null;
  matchupsLoading: boolean;

  /** Deck search: find published lists by the cards they run. */
  searchQuery: string;
  /** Cards matching `searchQuery`, offered as picks. Empty once one is chosen and the
   *  box is cleared, not left showing a stale match for text no longer in the box. */
  searchCandidates: Card[];
  /** The cards a result must run, all of them ("and", not "or"). */
  searchCards: Card[];
  searchSort: DeckSearchSort;
  searchResults: MetaDeck[];
  searchLoading: boolean;
  /** Distinguishes "loaded, nothing matched" from "nothing searched yet". */
  searchSearched: boolean;
}

export function emptyDeck(): DeckPayload {
  return {
    name: "Untitled Deck",
    format: "constructed",
    legendId: "",
    championId: "",
    main: {},
    runes: {},
    battlefields: [],
    sideboard: {},
  };
}

const initial: AppState = {
  ready: false,
  error: "",
  staleServer: "",
  view: "find",
  notice: "",
  formats: [],
  facets: null,

  deckId: "",
  deck: emptyDeck(),
  validation: null,
  dirty: false,
  savedDecks: [],

  availability: null,

  filters: {
    q: "",
    cardType: "",
    domain: "",
    setCode: "",
    rarity: "",
    availableOnly: false,
    sort: "availability",
  },
  cards: [],
  cardTotal: 0,
  cardsLoading: false,
  cardLimit: 24,
  builderReview: false,
  deckCards: new Map(),
  suggestions: null,
  opening: null,
  drawerOpen: false,
  benchSource: null,

  metaStatus: null,
  archetypes: [],
  metaDecks: [],
  metaLoading: false,
  metaArchetype: "",
  metaBuildableOnly: false,

  smartLegends: [],
  smartSession: null,
  smartAnswers: new Map(),
  smartTouched: new Set(),
  smartBusy: false,
  smartFinished: false,
  smartShowing: "rounds",
  smartResumable: [],
  smartLegendQuery: "",
  smartLegendSort: "strength",
  smartLegendsError: "",
  smartLegendsLoaded: false,
  smartLegendsAttempts: 0,
  smartLegendsRetrying: false,
  refresh: null,
  refreshBusy: false,

  exploreMode: "legends",
  cardTrends: null,
  cardDetail: null,
  exploreCardType: "",
  exploreDimension: "champion",
  exploreFormat: "",
  exploreFrom: "",
  exploreTo: "",
  exploreMinPlayers: DEFAULT_MIN_PLAYERS,
  exploreRange: "",
  exploreBucket: "week",
  trendOverview: null,
  championMeta: null,
  legendMeta: null,
  tournamentDetail: null,
  exploreLoading: false,
  exploreError: "",

  matchups: null,
  legendMatchups: null,
  matchupsLoading: false,

  searchQuery: "",
  searchCandidates: [],
  searchCards: [],
  searchSort: "rank",
  searchResults: [],
  searchLoading: false,
  searchSearched: false,
};

type Listener = (state: AppState) => void;

class Store {
  #state: AppState = initial;
  #listeners = new Set<Listener>();

  get state(): Readonly<AppState> {
    return this.#state;
  }

  set(patch: Partial<AppState>): void {
    this.#state = { ...this.#state, ...patch };
    for (const listener of this.#listeners) listener(this.#state);
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => this.#listeners.delete(listener);
  }
}

export const store = new Store();
